/**
 * Application route paths
 * Mirrors the Angular app's routing structure
 */
export const ROUTES = {
  SUPERADMIN_DATA_SYNC: '/superadmin/data-sync',
  HOME: '/',

  // Dashboard tabs
  DASHBOARD: {
    ROOT: '/dashboard',
    WIP: '/dashboard/wip',
    WORKLOAD: '/dashboard/workload',
    SUMMARY: '/dashboard/summary',
    TODO: '/dashboard/todo',
    ACTIVITIES: '/dashboard/activities',
    CALENDAR: '/dashboard/calendar',
    QUESTIONNAIRES: '/dashboard/questionnaires',
    QUESTIONNAIRE_EMBED: '/dashboard/q',
  },

  // Client Dashboard (client-facing workspace) — shares /dashboard; the shell
  // rendered there depends on whether the logged-in user has the client role.
  CLIENT: {
    ROOT: '/dashboard',
    REQUESTS: '/dashboard/requests',
    FRONT_DOOR: '/dashboard/front-door',
    DOCUMENTS: '/dashboard/documents',
    ACTIVITY: '/dashboard/activity',
  },

  // Add menu (actions/dialogs)
  ADD: {
    NEW_RECORD: '/add/new-record',
    NEW_JOB: '/jobs/new-workflow',
    NEW_SERVICE_REQUEST: '/jobs/new-service',
  },

  // People & Workgroups
  PEOPLE: {
    ROOT: '/people',
    DIRECTORY: '/people-workgroups', // NEW combined page
    ORGANIZATION: '/identity/organization-units',
    USERS: '/identity/users',
    PEOPLE_CHANGES: '/people-changes',
    IMPORT_USERS: '/people-workgroups/import',
  },

  // AI Management
  AI: {
    ROOT: '/ai',
    PROVIDERS: '/ai-providers',
    MODELS: '/ai-models',
    TASKS: '/ai-tasks',
    ASSISTANT: '/ai-assistant',
    KNOWLEDGE_BASE: '/ai-knowledge-base',
  },

  // AI Skills Library (Skills, MCP Servers, Packs)
  SKILLS_LIBRARY: {
    ROOT: '/admin/skills-library',
    SKILLS_LIST: '/admin/skills',
    SKILL_EDITOR: '/admin/skills/:id',
    MCP_SERVERS_LIST: '/admin/mcp-servers',
    MCP_SERVER_EDITOR: '/admin/mcp-servers/:id',
    PACKS_LIST: '/admin/packs',
    PACK_EDITOR: '/admin/packs/:id',
    PACK_ENABLEMENT: '/admin/packs/enablement',
  },

  // Templates
  TEMPLATES: {
    ROOT: '/templates',
    PROCESS_TYPES: '/admin/template-types',
    PROCESS_TEMPLATES: '/admin/process-templates',
    SNIPPETS: '/manage-snippets',
    KEY_MAPS: '/manage-key-maps',
    OUTCOMES: '/manage-job-outcomes',
    EMAIL_TEMPLATE_CONFIG: '/mail-templates',
    EMAIL_TEMPLATES: '/manage-email-templates',
    TAGS: '/manage-tags',
    DATA_FORMS: '/manage-data-forms',
    TEMPLATE_IMPORT: '/admin/template-import',
    DOCUMENT_TEMPLATES: '/manage-document-templates',
  },

  // Reports
  REPORTS: {
    ROOT: '/reports',
    POWER_BI: '/reports-power',
    POWER_BI_DETAILS: '/reports-power/details',
    USERS: '/reports/users',
    ACTIVITY: '/reports/activity',
    JOB_AUDIT: '/reports/job-history',
    JOB_TIME_LOGS: '/reports/duration-logs',
    PERFORMANCE: '/reports/system-performance-report',
    TAGS_PERFORMANCE: '/reports/tags-performance-report',
    PIPELINE: '/reports/pipeline',
    WORKGROUP_PIPELINE: '/reports/workgroup-pipeline',
    TEMPLATE_AUDIT: '/reports/template-audit',
    ND_RECORD_AUDIT: '/reports/new-record',
    JOB_OUTCOME: '/reports/joboutcome',
    DURATION: '/reports/duration-report',
    STEP_DURATION: '/reports/step-duration-report',
    TIME_BREAKDOWN: '/reports/time-breakdown',
    TAGS: '/reports/tags-report',
    SUGGESTED_IMPROVEMENTS: '/reports/suggested-improvements/improvements',
    CLIENT_FEEDBACK: '/reports/suggested-improvements/feedbacks',
    RISKS: '/reports/risks',
    AI_USAGE: '/reports/ai-usage',
    AI_ACTIVITY: '/reports/ai-activity',
    JOB_STEP_APPROVALS: '/reports/job-step-approvals',
    JOB_STEP_BASED_APPROVAL: '/reports/job-step-based-approval',
  },

  // Administration
  ADMIN: {
    ROOT: '/admin',
    IDENTITY: '/identity',
    SAAS: '/saas',
    OPENID: '/openiddict',
    LANGUAGE: '/language-management',
    LANGUAGE_TEXTS: '/language-management/texts',
    TEXT_TEMPLATES: '/text-template-management',
    AUDIT_LOGS: '/audit-logs',
    SETTINGS: '/setting-management',
    //API_KEYS: '/admin/api-keys',
    TENANTS: '/admin/tenants',
    ROLES: '/admin/roles',
    ENTITY_TYPES: '/entity-types',
    ENTITY_RECORDS: '/entity-records',
    ENTITY_RECORDS_BULK_IMPORT: '/entity-records/bulk-import',
  },

  // Settings Hub
  SETTINGS: {
    ROOT: '/settings',
    GENERAL: '/settings/general',
    AI: '/settings/ai',
    DOCUSIGN: '/settings/docusign',
    NETDOCUMENTS: '/settings/netdocuments',
    POWERBI: '/settings/powerbi',
    TENANT: '/settings/tenant',
    DOCUMENT_REPO: '/settings/document-repo',
    MOTRQ: '/settings/motrq',
    SYNCED_DATA: '/synced-data',
    BULK_IMPORT: '/synced-data/:cabinetId/bulk-import',
  },

  // My Approvals
  MY_APPROVALS: '/my-approvals',

  // Authority Delegation (any authenticated user)
  AUTHORITY_DELEGATION: '/authority-delegation',

  // Messages
  MESSAGES: '/messages',

  // Auth callbacks
  LOGIN: '/login',
  CALLBACK: '/callback',
  LOGOUT_CALLBACK: '/logout-callback',

  // DocuSign signing callback
  // Backend constructs: {baseUrl}/wip-dashboard/j/{jobId}/s/{stepId}/e/{envelopeId}/signed
  DOCUSIGN_CALLBACK: '/wip-dashboard/j/:jobId/s/:stepId/e/:envelopId/:method',
} as const;

/**
 * Permission constants for route guards
 * Mirrors the Angular app's permission structure
 */
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD: {
    HOME: 'motr.Dashboard.HomePage',
    WIP: 'motr.Dashboard.Wip',
    SERVICE_REQUESTS: 'motr.Dashboard.ServiceRequests',
    WORKLOAD: 'motr.Dashboard.Workload',
    SUMMARY: 'motr.Dashboard.Summary',
    TODO: 'motr.Dashboard.Todo',
    ACTIVITIES: 'motr.Dashboard.RecentActivity',
    CALENDAR: 'motr.Dashboard.Calendar',
  },

  // Client Dashboard (top-level, peer of Dashboard). Parent key reused so
  // existing grants of motr.Dashboard.Client carry over. Children map 1:1 to
  // the client workspace tabs.
  CLIENT_DASHBOARD: {
    DEFAULT: 'motr.Dashboard.Client',
    MY_REQUESTS: 'motr.Dashboard.Client.MyRequests',
    SERVICE_REQUESTS: 'motr.Dashboard.Client.ServiceRequests',
    DOCUMENTS: 'motr.Dashboard.Client.Documents',
    ACTIVITY: 'motr.Dashboard.Client.Activity',
  },

  // Add menu
  ADD: {
    NEW_RECORD: 'motr.AddNewRecords.AddtoND',
    NEW_JOB: 'motr.AddNewRecords.AddNewWorkflow',
    NEW_SERVICE_REQUEST: 'motr.AddNewRecords.AddServiceRequest',
  },

  // motrQ
  MOTRQ: {
    SUBMIT_QUESTIONNAIRE: 'motr.motrQ.SubmitQuestionnaire',
  },

  // People & Workgroups
  PEOPLE: {
    ORGANIZATION: 'motr.Settings.OrganizationManagement',
    USERS: 'motr.Settings.PeopleManagement',
    PEOPLE_CHANGES: 'motr-functional.User.AddRemoveReplaceUser',
    IMPORT_USERS: 'motr-functional.User.ImportUsers',
  },

  // AI Management
  AI: {
    PROVIDERS: 'motr.AiProviders',
    MODELS: 'motr.AiModels',
    TASKS: 'motr.AiTasks',
    ASSISTANT: 'motr.AskAssistant',
    KNOWLEDGE_BASE: 'motr.KnowledgeBase',
  },

  // AI Skills Library — Functional permissions (feature-level).
  // These are the strings hand-authored React + Extended app-service code
  // checks against. Suite-generated CRUD perms (motr.Skills, motr.McpServers,
  // motr.Packs) are still enforced by Suite-managed base classes — that's
  // tracked separately and granted to roles alongside these Functional perms.
  SKILLS: {
    VIEW: 'motr-functional.SkillsLibrary',
    MANAGE: 'motr-functional.SkillsLibrary.Skills.Manage',
    PUBLISH: 'motr-functional.SkillsLibrary.Skills.Publish',
    MCP_MANAGE: 'motr-functional.SkillsLibrary.McpServers.Manage',
    PACKS_MANAGE: 'motr-functional.SkillsLibrary.Packs.Manage',
    PACKS_PUBLISH: 'motr-functional.SkillsLibrary.Packs.Publish',
    PACKS_ENABLE: 'motr-functional.SkillsLibrary.Packs.Enable',
    // Back-compat aliases — kept so existing references keep compiling while
    // we migrate. New code should use VIEW/MANAGE/PUBLISH above.
    CREATE: 'motr-functional.SkillsLibrary.Skills.Manage',
    EDIT: 'motr-functional.SkillsLibrary.Skills.Manage',
    DELETE: 'motr-functional.SkillsLibrary.Skills.Manage',
  },

  // Templates
  TEMPLATES: {
    TYPES: 'motr.TemplateTypes',
    TEMPLATES: 'motr.Templates',
    SNIPPETS: 'motr.Snippets.Create',
    KEY_MAPS: 'motr.KeyMaps.Create',
    OUTCOMES: 'motr.JobOutcomes.Create',
    EMAIL_TEMPLATES: 'motr.EmailTemplates.Create',
    TAGS: 'motr.Tags.Create',
    TAG_CATEGORIES: 'motr.TagCategories.Create',
    DATA_FORMS: 'motr-actions.Templates.DataForms',
    DOCUMENT_TEMPLATES: 'motr.DocumentTemplates',
  },

  // Reports
  REPORTS: {
    POWER_BI: 'motr.Reports.AllowViewPowerBIReports',
    USERS: 'motr.Reports.UserReport',
    JOB_HISTORY: 'motr.Reports.JobHistory',
    PERFORMANCE: 'motr.Reports.Performance',
    PIPELINE: 'motr.Reports.Pipeline',
    WORKGROUP_PIPELINE: 'motr.Reports.WorkgroupPipeline',
    TEMPLATE_AUDIT: 'motr.Reports.TemplateAudit',
    ND_RECORD_AUDIT: 'motr.Reports.NDRecordAudit',
    JOB_OUTCOMES: 'motr.Reports.JobOutcomes',
    DURATION: 'motr.Reports.Duration',
    TIME_BREAKDOWN: 'motr.Reports.TimeBreakdown',
    TAGS: 'motr.Reports.Tags',
    SUGGESTED_IMPROVEMENTS: 'motr.Reports.SuggestedImprovements',
    RISKS: 'motr.Reports.Risks',
    AI_USAGE_VIEW_ALL: 'motr.Reports.AiUsage.ViewAll',
    AI_USAGE_VIEW_OWN: 'motr.Reports.AiUsage.ViewOwn',
    AI_ACTIVITY_VIEW: 'motr.Reports.AiActivity.View',
    JOB_STEP_APPROVALS: 'motr.Reports.JobStepApprovals',
    JOB_STEP_BASED_APPROVAL: 'motr.Reports.JobStepBasedApproval',
    ACTIVITY: 'motr.Reports.ActivityReport',
  },

  // Jobs
  JOBS: {
    SUGGEST_IMPROVEMENT: 'motr.Jobs.SuggestImprovement',
  },

  // Administration
  ADMIN: {
    IDENTITY: 'AbpIdentity.Users',
    SAAS: 'Saas.Tenants',
    SAAS_IMPERSONATE: 'Saas.Tenants.Impersonation',
    OPENID: 'OpenIddictPro.Application',
    LANGUAGE: 'LanguageManagement.Languages',
    LANGUAGE_TEXTS: 'LanguageManagement.LanguageTexts',
    TEXT_TEMPLATES: 'TextTemplateManagement.TextTemplates',
    AUDIT_LOGS: 'AuditLogging.ViewChangeHistory',
    SETTINGS: 'SettingManagement.Emailing',
    //API_KEYS: 'motr.ApiKeys',
    ROLES: 'AbpIdentity.Roles',
    ROLES_CREATE: 'AbpIdentity.Roles.Create',
    ROLES_UPDATE: 'AbpIdentity.Roles.Update',
    ROLES_DELETE: 'AbpIdentity.Roles.Delete',
    ROLES_MANAGE_PERMISSIONS: 'AbpIdentity.Roles.ManagePermissions',
    // Screen-level gates for Administration > Entity Types / Entity Records.
    // The Suite-generated motr.EntityTypes / motr.EntityRecords CRUD permissions
    // still authorize the app services; these only decide who sees the screens.
    ENTITY_TYPES: 'motr-functional.EntityTypes',
    ENTITY_RECORDS: 'motr-functional.EntityRecords',
  },

  // Settings
  SETTINGS: {
    GENERAL: 'motr.Settings.Settings',
    POWER_BI: 'motr.Reports.AllowManagePowerBIReports',
    TENANT: 'motr.Application.AllowManageTenantSettings',
    DOCUMENT_REPO: 'motr.Settings.DocumentManagement',
    CABINET: 'motr.Settings.NetDocumentsSettings',
    AI: 'motr.Settings.Settings',
    MOTRQ: 'motr.motrQ.ManageQuestionnaire',
  },

  // My Approvals
  MY_APPROVALS: 'motr.Approvals',

  // Messages
  MESSAGES: 'motr.Messages',
} as const;
